import { useEffect, useState } from "react";
import { api, type Scheme } from "@/lib/api";
import { BenefitChip, Chip, LevelChip } from "@/lib/chips";

interface Props {
  schemeId: string | null;
  onClose: () => void;
  onAskAi: (prompt: string) => void;
}

/**
 * Scheme detail modal — "paper card" pulled up on either theme.
 * The card itself is always cream so text colors are hard-coded
 * dark-ink (readable regardless of light/dark page).
 */
export function SchemeDetailModal({ schemeId, onClose, onAskAi }: Props) {
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [err, setErr]       = useState<string | null>(null);

  useEffect(() => {
    if (!schemeId) { setScheme(null); setErr(null); return; }
    let active = true;
    api.scheme(schemeId)
      .then((s) => { if (active) setScheme(s); })
      .catch((e) => { if (active) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { active = false; };
  }, [schemeId]);

  useEffect(() => {
    document.body.style.overflow = schemeId ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [schemeId]);

  if (!schemeId) return null;

  const level = scheme?.level ? scheme.level[0].toUpperCase() + scheme.level.slice(1) : null;
  const meta  = [scheme?.ministry, scheme?.state, level].filter(Boolean).join(" · ");

  return (
    <div
      className="fixed inset-0 bg-black/55 backdrop-blur-sm z-40 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="paper-card max-w-3xl mx-auto rounded-2xl shadow-xl my-10 overflow-hidden"
        style={{ background: "#fefae0", color: "#1a0c10" }}
      >
        <div
          className="flex items-start justify-between p-6 gap-4"
          style={{ borderBottom: "1px solid #e8dfbc" }}
        >
          <div className="min-w-0">
            {scheme && (
              <div className="flex flex-wrap gap-1 mb-2">
                <LevelChip level={scheme.level} />
                <BenefitChip kind={scheme.benefit_type} />
                {scheme.audience && <Chip>{scheme.audience}</Chip>}
                {(scheme.tags || []).slice(0, 6).map((t) => <Chip key={t}>{t}</Chip>)}
              </div>
            )}
            <h2
              className="text-2xl font-semibold font-display leading-tight"
              style={{ color: "#1a0c10" }}
            >
              {scheme?.name || (err ? `Error loading scheme` : "Loading…")}
            </h2>
            {meta && (
              <p className="text-sm mt-1" style={{ color: "#6a5a56" }}>
                {meta}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-2xl leading-none transition-colors"
            style={{ color: "#6a5a56" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#1a0c10")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#6a5a56")}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="p-6 space-y-5">
          {err && <div className="text-sm" style={{ color: "#c62839" }}>{err}</div>}
          {scheme && (
            <>
              <Section title="Summary"            text={scheme.summary} />
              <Section title="Benefits"           text={scheme.benefits} />
              <Section title="Eligibility"        text={scheme.eligibility} />
              <Section title="How to apply"       text={scheme.how_to_apply} />
              <Section title="Documents required" text={scheme.documents} />

              <div className="flex flex-wrap gap-3 pt-2">
                {scheme.application_url && (
                  <a
                    href={scheme.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-full transition"
                    style={{ background: "#c62839", color: "#fefae0" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#e63946")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#c62839")}
                  >
                    Apply on official portal
                  </a>
                )}
                {scheme.source_url && (
                  <a
                    href={scheme.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-full transition"
                    style={{ border: "1px solid #c8b99a", color: "#1a0c10" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#1a0c10")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#c8b99a")}
                  >
                    Source
                  </a>
                )}
                <button
                  onClick={() => {
                    onAskAi(
                      `Tell me how to apply to "${scheme.name}" — eligibility, steps, documents, ` +
                        `what to watch out for. Be concrete.`,
                    );
                    onClose();
                  }}
                  className="inline-flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-full transition"
                  style={{ background: "#1a0c10", color: "#fefae0" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#3a2026")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#1a0c10")}
                >
                  Explain this
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, text }: { title: string; text?: string | null }) {
  if (!text || !text.trim()) return null;
  return (
    <section>
      <h3
        className="font-semibold text-[11px] uppercase tracking-[0.22em] mb-1.5 font-mono"
        style={{ color: "#c62839" }}
      >
        {title}
      </h3>
      <div className="scheme-body" style={{ color: "#1a0c10" }}>
        {text}
      </div>
    </section>
  );
}
